import { useEffect, useRef } from 'react'

interface Point {
  x: number
  y: number
  life: number
}

export function MouseTrail() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const isTouch = window.matchMedia('(pointer: coarse)').matches
    if (isTouch) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let points: Point[] = []
    let raf = 0
    // `target` is where the mouse actually is; `cursor` chases it a little
    // behind. Sampling the chaser once per frame — rather than pushing raw
    // mousemove coordinates — is what makes the line smooth: pointer events
    // arrive in irregular bursts, so using them directly produces unevenly
    // spaced points and visible kinks on fast movement.
    let targetX = 0
    let targetY = 0
    let cursorX = 0
    let cursorY = 0
    let seeded = false
    let lastFrame = performance.now()

    const EASE = 0.22
    const FADE_PER_SECOND = 1.5
    // Generous enough that points always die of old age rather than being
    // sliced off the end of the buffer — on a 120Hz display the old cap of
    // 70 truncated the tail mid-life, which read as the line ending in a
    // hard stub instead of fading out.
    const MAX_POINTS = 160
    const MAX_WIDTH = 7

    /** Eased 0..1 ramp with zero slope at both ends, so the tail approaches
     * nothing gradually instead of stepping down to a visible stub. */
    const smoothstep = (v: number) => {
      const t = Math.min(1, Math.max(0, v))
      return t * t * (3 - 2 * t)
    }

    function resize() {
      canvas!.width = window.innerWidth * window.devicePixelRatio
      canvas!.height = window.innerHeight * window.devicePixelRatio
      canvas!.style.width = `${window.innerWidth}px`
      canvas!.style.height = `${window.innerHeight}px`
      // setTransform (not scale) so repeated resizes don't compound.
      ctx!.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    function onMove(e: MouseEvent) {
      targetX = e.clientX
      targetY = e.clientY
      // Start the chaser at the first real position, otherwise the trail
      // whips in from the top-left corner on the very first movement.
      if (!seeded) {
        cursorX = targetX
        cursorY = targetY
        seeded = true
      }
      // The loop sleeps while the mouse is still; the first move wakes it.
      if (raf === 0) {
        lastFrame = performance.now()
        raf = requestAnimationFrame(tick)
      }
    }
    window.addEventListener('mousemove', onMove, { passive: true })

    function tick(now: number) {
      // Clamp dt so a backgrounded tab doesn't return and wipe the trail in
      // one enormous step.
      const dt = Math.min((now - lastFrame) / 1000, 0.05)
      lastFrame = now

      ctx!.clearRect(0, 0, window.innerWidth, window.innerHeight)

      if (seeded) {
        // Frame-rate independent easing: the same visual follow speed on a
        // 60Hz and a 120Hz display.
        const t = 1 - Math.pow(1 - EASE, dt * 60)
        cursorX += (targetX - cursorX) * t
        cursorY += (targetY - cursorY) * t

        const last = points[points.length - 1]
        // Skip near-duplicate samples so an idle pointer doesn't stack up
        // zero-length segments that render as one bright blob.
        if (!last || Math.hypot(cursorX - last.x, cursorY - last.y) > 0.6) {
          points.push({ x: cursorX, y: cursorY, life: 1 })
          if (points.length > MAX_POINTS) points = points.slice(-MAX_POINTS)
        }
      }

      for (const p of points) p.life -= dt * FADE_PER_SECOND
      points = points.filter((p) => p.life > 0)

      if (points.length > 2) {
        ctx!.lineCap = 'round'
        ctx!.lineJoin = 'round'

        // Draw through the midpoints with quadratic curves: each raw point
        // becomes a control point rather than a corner, so the path reads as
        // one continuous stroke instead of a chain of straight segments.
        for (let i = 1; i < points.length - 1; i++) {
          const p = points[i]
          const next = points[i + 1]
          const prev = points[i - 1]

          if (p === undefined || prev === undefined || next === undefined) continue

          const midA = { x: (prev.x + p.x) / 2, y: (prev.y + p.y) / 2 }
          const midB = { x: (p.x + next.x) / 2, y: (p.y + next.y) / 2 }

          // Taper by position along the trail *and* by age, both eased, so
          // the oldest end thins and dims to nothing on a curve rather than
          // winking out at a fixed minimum width.
          const along = i / (points.length - 1)
          const strength = smoothstep(along) * smoothstep(p.life)
          const alpha = strength * 0.9
          if (alpha <= 0.002) continue

          const gradient = ctx!.createLinearGradient(midA.x, midA.y, midB.x, midB.y)
          gradient.addColorStop(0, `rgba(252, 34, 101, ${alpha})`)
          gradient.addColorStop(1, `rgba(255, 138, 61, ${alpha * 0.8})`)
          ctx!.strokeStyle = gradient
          // No minimum width: the stroke is allowed to reach zero.
          ctx!.lineWidth = strength * MAX_WIDTH
          // Fade the glow with the stroke — a constant-strength shadow made
          // the faintest tail segments look heavier than the line itself.
          ctx!.shadowColor = `rgba(252, 34, 101, ${strength * 0.55})`
          ctx!.shadowBlur = 14 * strength

          ctx!.beginPath()
          ctx!.moveTo(midA.x, midA.y)
          ctx!.quadraticCurveTo(p.x, p.y, midB.x, midB.y)
          ctx!.stroke()
        }
        ctx!.shadowBlur = 0
      }

      // Nothing left to draw and the chaser has caught up: stop asking for
      // frames. A loop that ran forever redrew a full-screen canvas sixty to
      // a hundred and twenty times a second for as long as the tab was open.
      const settled = Math.hypot(targetX - cursorX, targetY - cursorY) < 0.5
      raf = points.length === 0 && settled ? 0 : requestAnimationFrame(tick)
    }

    return () => {
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', onMove)
      cancelAnimationFrame(raf)
    }
  }, [])

  return <canvas ref={canvasRef} id="mouse-trail-canvas" aria-hidden="true" />
}
